import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { auth, db } from "./firebase";
import { EmptyState, PageHeader, StatCard, ToneBadge } from "./petaarUi";

const SWEEP_PENDING_BOOKINGS_URL =
  process.env.REACT_APP_SWEEP_PENDING_BOOKINGS_URL ||
  "https://us-central1-petaar-3dcab.cloudfunctions.net/sweepPendingBookings";
const ADMIN_UPDATE_OPS_URL =
  process.env.REACT_APP_ADMIN_UPDATE_OPS_URL ||
  "https://us-central1-petaar-3dcab.cloudfunctions.net/adminUpdateOps";
const PAYMENT_OPS_URL =
  process.env.REACT_APP_PAYMENT_OPS_URL ||
  "https://us-central1-petaar-3dcab.cloudfunctions.net/paymentOps";
const PAYOUT_OPS_URL =
  process.env.REACT_APP_PAYOUT_OPS_URL ||
  "https://us-central1-petaar-3dcab.cloudfunctions.net/payoutOps";

export default function AdminDashboard({ onLogout }) {
  const [providers, setProviders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [featuredRequests, setFeaturedRequests] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [reviewReports, setReviewReports] = useState([]);
  const [moderationLogs, setModerationLogs] = useState([]);
  const [vetApplications, setVetApplications] = useState([]);
  const [partnerApplications, setPartnerApplications] = useState([]);
  const [reviewingVetProviderId, setReviewingVetProviderId] = useState("");
  const [paymentHealth, setPaymentHealth] = useState(null);
  const [paymentHealthError, setPaymentHealthError] = useState("");
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [payoutLoadError, setPayoutLoadError] = useState("");
  const [updatingPayoutId, setUpdatingPayoutId] = useState("");
  const [moderatingReportId, setModeratingReportId] = useState("");
  const [loading, setLoading] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const [bookingStatusFilter, setBookingStatusFilter] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");
  const [refundStatusFilter, setRefundStatusFilter] = useState("");
  const [payoutStatusFilter, setPayoutStatusFilter] = useState("");
  const [revenuePeriodFilter, setRevenuePeriodFilter] = useState("all");
  const [bookingsPage, setBookingsPage] = useState(1);
  const [servicesPage, setServicesPage] = useState(1);
  const [payoutsPage, setPayoutsPage] = useState(1);
  const [loadError, setLoadError] = useState("");
  const adminPageSize = 20;

  const exportBookingsCsv = () => {
    const filteredRows = filteredAdminBookings
      .map((b) => ({
        bookingId: b.id,
        serviceTitle: b.serviceTitle || "",
        ownerEmail: b.ownerEmail || b.userEmail || "",
        providerEmail: b.providerEmail || "",
        amount: Number(b.amount) || 0,
        paymentStatus: b.paymentStatus || "",
        bookingStatus: b.status || "",
        refundStatus: b.refundStatus || "not_requested",
        bookingDate: b.bookingDate || "",
        bookingSlot: b.bookingSlot || "",
      }));

    if (!filteredRows.length) {
      alert("No records to export.");
      return;
    }

    const headers = Object.keys(filteredRows[0]);
    const lines = [
      headers.join(","),
      ...filteredRows.map((row) =>
        headers.map((h) => {
          const text = String(row[h] ?? "");
          if (text.includes(",") || text.includes('"') || text.includes("\n")) {
            return `"${text.replaceAll('"', '""')}"`;
          }
          return text;
        }).join(","),
      ),
    ];
  const blob = new Blob([lines.join("\n")], {type: "text/csv;charset=utf-8;"});
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `admin-bookings-${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  };

  const fetchPaymentHealth = useCallback(async () => {
    try {
      setPaymentHealthError("");
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error("Admin session expired. Please login again.");
      }
      const res = await fetch(PAYMENT_OPS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ action: "health", limit: 40 }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || `Payment health API failed (${res.status})`);
      }
      setPaymentHealth(data);
    } catch (error) {
      setPaymentHealth(null);
      setPaymentHealthError(error.message || "Unable to load payment health.");
    }
  }, []);

  const callPayoutApi = useCallback(async (payload) => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error("Admin session expired. Please login again.");
    }
    const res = await fetch(PAYOUT_OPS_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.success) {
      throw new Error(data?.error || `Payout API failed (${res.status})`);
    }
    return data;
  }, []);

  const fetchPayoutRequests = useCallback(async () => {
    try {
      setPayoutLoadError("");
      const data = await callPayoutApi({
        action: "admin_list",
        limit: 150,
      });
      setPayoutRequests(Array.isArray(data.requests) ? data.requests : []);
    } catch (error) {
      setPayoutRequests([]);
      setPayoutLoadError(error.message || "Unable to load payout requests.");
    }
  }, [callPayoutApi]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      setLoadError("");
      const [
        usersSnap,
        bookingSnap,
        serviceSnap,
        featuredSnap,
        subscriptionsSnap,
        supportSnap,
        reviewReportsSnap,
        moderationLogsSnap,
        vetApplicationsSnap,
        partnerApplicationsSnap,
      ] = await Promise.all([
        getDocs(collection(db, "users")),
        getDocs(collection(db, "bookings")),
        getDocs(collection(db, "services")),
        getDocs(collection(db, "featuredRequests")),
        getDocs(collection(db, "subscriptions")),
        getDocs(collection(db, "supportTickets")),
        getDocs(collection(db, "reviewReports")),
        getDocs(collection(db, "moderationLogs")),
        getDocs(collection(db, "vetApplications")),
        getDocs(collection(db, "partnerApplications")),
      ]);
      const allUsers = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const providerLikeUsers = allUsers.filter((item) => {
        const role = String(item.role || "").toLowerCase();
        const status = String(item.providerStatus || "").toLowerCase();
        const onboarding = String(item.partnerOnboardingStatus || "").toLowerCase();
        return (
          role === "provider" ||
          ["pending", "pending_review", "approved", "rejected", "suspended"].includes(status) ||
          onboarding === "pending_review" ||
          Boolean(item.partnerProfile)
        );
      });
      setProviders(providerLikeUsers);
      setBookings(bookingSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setServices(serviceSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setFeaturedRequests(featuredSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setSubscriptions(subscriptionsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setSupportTickets(supportSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setReviewReports(reviewReportsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setModerationLogs(moderationLogsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setVetApplications(vetApplicationsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setPartnerApplications(partnerApplicationsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      await Promise.all([
        fetchPaymentHealth(),
        fetchPayoutRequests(),
      ]);
    } catch (error) {
      setLoadError(error.message || "Unable to load admin data.");
    } finally {
      setLoading(false);
    }
  }, [fetchPaymentHealth, fetchPayoutRequests]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const providerStats = useMemo(() => {
    const approved = providers.filter((p) => String(p.providerStatus || "").toLowerCase() === "approved").length;
    const pending = providers.filter((p) => {
      const status = String(p.providerStatus || "").toLowerCase();
      return !status || status === "pending" || status === "pending_review";
    }).length;
    const rejected = providers.filter((p) => String(p.providerStatus || "").toLowerCase() === "rejected").length;
    return { approved, pending, rejected };
  }, [providers]);
  const pendingPartnerApplications = useMemo(
    () => partnerApplications.filter((item) => String(item.status || "").toLowerCase() === "pending_review").length,
    [partnerApplications],
  );

  const getBookingTs = (booking) => {
    if (booking?.paidAt?.seconds) return booking.paidAt.seconds * 1000;
    if (booking?.createdAt?.seconds) return booking.createdAt.seconds * 1000;
    if (booking?.bookingDate) {
      const parsed = new Date(booking.bookingDate).getTime();
      if (!Number.isNaN(parsed)) return parsed;
    }
    return 0;
  };
  const revenueScopedBookings = useMemo(() => {
    const days = revenuePeriodFilter === "7d" ? 7 : revenuePeriodFilter === "30d" ? 30 : revenuePeriodFilter === "90d" ? 90 : 0;
    if (!days) return bookings;
    const fromTs = Date.now() - days * 24 * 60 * 60 * 1000;
    return bookings.filter((b) => {
      const ts = getBookingTs(b);
      return ts ? ts >= fromTs : false;
    });
  }, [bookings, revenuePeriodFilter]);
  const paidBookings = useMemo(
    () => revenueScopedBookings.filter((b) => b.paymentStatus === "paid"),
    [revenueScopedBookings],
  );
  const refundedBookings = useMemo(
    () => revenueScopedBookings.filter((b) => b.refundStatus === "refund_processed"),
    [revenueScopedBookings],
  );
  const pendingFeaturedRequests = useMemo(
    () => featuredRequests.filter((r) => String(r.status || "pending") === "pending").length,
    [featuredRequests],
  );
  const supportTicketStats = useMemo(() => {
    const byStatus = {
      open: 0,
      in_progress: 0,
      resolved: 0,
      closed: 0,
    };
    supportTickets.forEach((ticket) => {
      const key = String(ticket.status || "open").toLowerCase();
      if (byStatus[key] === undefined) {
        byStatus.open += 1;
      } else {
        byStatus[key] += 1;
      }
    });
    return {
      total: supportTickets.length,
      ...byStatus,
    };
  }, [supportTickets]);
  const reviewReportStats = useMemo(() => {
    const pending = reviewReports.filter((item) => String(item.status || "pending") === "pending").length;
    const actioned = reviewReports.filter((item) => String(item.status || "") === "actioned").length;
    const dismissed = reviewReports.filter((item) => String(item.status || "") === "dismissed").length;
    const restored = reviewReports.filter((item) => String(item.status || "") === "restored").length;
    return {
      total: reviewReports.length,
      pending,
      actioned,
      dismissed,
      restored,
    };
  }, [reviewReports]);
  const payoutStats = useMemo(() => {
    const totalAmount = payoutRequests.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
    const requested = payoutRequests.filter((item) => String(item.status || "") === "requested");
    const approved = payoutRequests.filter((item) => String(item.status || "") === "approved");
    const paid = payoutRequests.filter((item) => String(item.status || "") === "paid");
    const rejected = payoutRequests.filter((item) => String(item.status || "") === "rejected");
    return {
      totalCount: payoutRequests.length,
      totalAmount,
      requestedCount: requested.length,
      requestedAmount: requested.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
      approvedCount: approved.length,
      approvedAmount: approved.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
      paidCount: paid.length,
      paidAmount: paid.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
      rejectedCount: rejected.length,
    };
  }, [payoutRequests]);
  const assignmentWatchlist = useMemo(() => {
    const nowMs = Date.now();
    return bookings
      .filter((item) => {
        const status = String(item.status || "").toLowerCase();
        if (!["pending_assignment", "pending"].includes(status)) return false;
        if (String(item.paymentStatus || "").toLowerCase() !== "paid") return false;
        const assignedTs =
          item.assignedAt?.seconds ? item.assignedAt.seconds * 1000 :
            item.updatedAt?.seconds ? item.updatedAt.seconds * 1000 :
              item.createdAt?.seconds ? item.createdAt.seconds * 1000 : 0;
        if (!assignedTs) return false;
        const waitMins = (nowMs - assignedTs) / (60 * 1000);
        return waitMins >= 10;
      })
      .map((item) => {
        const assignedTs =
          item.assignedAt?.seconds ? item.assignedAt.seconds * 1000 :
            item.updatedAt?.seconds ? item.updatedAt.seconds * 1000 :
              item.createdAt?.seconds ? item.createdAt.seconds * 1000 : 0;
        const waitMins = assignedTs ? Math.round((nowMs - assignedTs) / (60 * 1000)) : 0;
        return {
          ...item,
          waitMins,
        };
      })
      .sort((a, b) => (b.waitMins || 0) - (a.waitMins || 0))
      .slice(0, 20);
  }, [bookings]);
  const subscriptionStats = useMemo(() => {
    const now = Date.now();
    const active = subscriptions.filter((item) => {
      const endMs = Date.parse(String(item.endAtIso || ""));
      return String(item.status || "").toLowerCase() === "active" && Number.isFinite(endMs) && endMs > now;
    });
    const expired = subscriptions.filter((item) => String(item.status || "").toLowerCase() === "expired");
    const cancelled = subscriptions.filter((item) => String(item.status || "").toLowerCase() === "cancelled");
    const featured = subscriptions.filter((item) => String(item.planType || "").toLowerCase() === "featured_listing");
    const aiPremium = subscriptions.filter((item) => String(item.planType || "").toLowerCase() === "ai_premium");
    const revenue = subscriptions.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
    return {
      total: subscriptions.length,
      active: active.length,
      expired: expired.length,
      cancelled: cancelled.length,
      featured: featured.length,
      aiPremium: aiPremium.length,
      revenue,
    };
  }, [subscriptions]);
  const revenueStats = useMemo(() => {
    const grossPaid = paidBookings.reduce((sum, b) => sum + (Number(b.amount) || 0), 0);
    const refunded = refundedBookings.reduce((sum, b) => sum + (Number(b.amount) || 0), 0);
    const netPaid = Math.max(0, grossPaid - refunded);
    const platformCommission = Math.round(netPaid * 0.2);
    const providerPayout = Math.max(0, netPaid - platformCommission);
    return {
      grossPaid,
      refunded,
      netPaid,
      platformCommission,
      providerPayout,
    };
  }, [paidBookings, refundedBookings]);
  const monthlyRevenue = useMemo(() => {
    const map = new Map();
    const toMonthInfo = (value) => {
      if (!value) return "Unknown";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return { key: "unknown", label: "Unknown", sortTs: 0 };
      const start = new Date(d.getFullYear(), d.getMonth(), 1);
      return {
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
        label: d.toLocaleString("en-IN", { month: "short", year: "numeric" }),
        sortTs: start.getTime(),
      };
    };

    revenueScopedBookings.forEach((b) => {
      if (b.paymentStatus !== "paid") return;
      const info = toMonthInfo(b.bookingDate || b.createdAt?.toDate?.());
      const amount = Number(b.amount) || 0;
      const current = map.get(info.key) || { gross: 0, refunded: 0, label: info.label, sortTs: info.sortTs };
      current.gross += amount;
      map.set(info.key, current);
    });

    revenueScopedBookings.forEach((b) => {
      if (b.refundStatus !== "refund_processed") return;
      const info = toMonthInfo(b.bookingDate || b.createdAt?.toDate?.());
      const amount = Number(b.amount) || 0;
      const current = map.get(info.key) || { gross: 0, refunded: 0, label: info.label, sortTs: info.sortTs };
      current.refunded += amount;
      map.set(info.key, current);
    });

    return Array.from(map.values())
      .map((value) => {
        const net = Math.max(0, value.gross - value.refunded);
        return {
          month: value.label,
          sortTs: value.sortTs,
          gross: value.gross,
          refunded: value.refunded,
          net,
          commission: Math.round(net * 0.2),
          payout: Math.max(0, net - Math.round(net * 0.2)),
        };
      })
      .sort((a, b) => b.sortTs - a.sortTs)
      .slice(0, 6);
  }, [revenueScopedBookings]);
  const filteredAdminBookings = useMemo(
    () => bookings
      .filter((b) => (bookingStatusFilter ? b.status === bookingStatusFilter : true))
      .filter((b) => (paymentStatusFilter ? b.paymentStatus === paymentStatusFilter : true))
      .filter((b) => (refundStatusFilter ? b.refundStatus === refundStatusFilter : true)),
    [bookings, bookingStatusFilter, paymentStatusFilter, refundStatusFilter],
  );
  const totalBookingPages = Math.max(1, Math.ceil(filteredAdminBookings.length / adminPageSize));
  const pagedAdminBookings = useMemo(() => {
    const start = (bookingsPage - 1) * adminPageSize;
    return filteredAdminBookings.slice(start, start + adminPageSize);
  }, [filteredAdminBookings, bookingsPage]);
  const sortedServices = useMemo(
    () => [...services].sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)),
    [services],
  );
  const totalServicePages = Math.max(1, Math.ceil(sortedServices.length / adminPageSize));
  const pagedServices = useMemo(() => {
    const start = (servicesPage - 1) * adminPageSize;
    return sortedServices.slice(start, start + adminPageSize);
  }, [servicesPage, sortedServices]);
  const filteredPayoutRequests = useMemo(
    () => payoutRequests.filter((item) => (payoutStatusFilter ? String(item.status || "") === payoutStatusFilter : true)),
    [payoutRequests, payoutStatusFilter],
  );
  const sortedFilteredPayoutRequests = useMemo(
    () => [...filteredPayoutRequests].sort(
      (a, b) => ((b.createdAt?._seconds || b.createdAt?.seconds || 0) - (a.createdAt?._seconds || a.createdAt?.seconds || 0)),
    ),
    [filteredPayoutRequests],
  );
  const totalPayoutPages = Math.max(1, Math.ceil(sortedFilteredPayoutRequests.length / adminPageSize));
  const pagedPayoutRequests = useMemo(() => {
    const start = (payoutsPage - 1) * adminPageSize;
    return sortedFilteredPayoutRequests.slice(start, start + adminPageSize);
  }, [payoutsPage, sortedFilteredPayoutRequests]);
  useEffect(() => {
    setBookingsPage(1);
  }, [bookingStatusFilter, paymentStatusFilter, refundStatusFilter]);
  useEffect(() => {
    setPayoutsPage(1);
  }, [payoutStatusFilter]);
  useEffect(() => {
    if (bookingsPage > totalBookingPages) setBookingsPage(totalBookingPages);
  }, [bookingsPage, totalBookingPages]);
  useEffect(() => {
    if (servicesPage > totalServicePages) setServicesPage(totalServicePages);
  }, [servicesPage, totalServicePages]);
  useEffect(() => {
    if (payoutsPage > totalPayoutPages) setPayoutsPage(totalPayoutPages);
  }, [payoutsPage, totalPayoutPages]);

  const callAdminApi = async (payload) => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error("Admin session expired. Please login again.");
    }
    const res = await fetch(ADMIN_UPDATE_OPS_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.success) {
      throw new Error(data?.error || `Admin API failed (${res.status})`);
    }
    return data;
  };

  const setProviderStatus = async (providerId, providerStatus) => {
    try {
      await callAdminApi({
        action: "provider_status",
        providerId,
        providerStatus,
      });
      await fetchAll();
    } catch (error) {
      alert(error.message);
    }
  };

  const reviewVetApplication = async (providerId, status) => {
    if (!providerId) return;
    try {
      setReviewingVetProviderId(providerId);
      const note = status === "rejected" ? (window.prompt("Rejection note (optional)") || "") : "";
      await callAdminApi({
        action: "vet_application_review",
        providerId,
        status,
        note,
      });
      await fetchAll();
    } catch (error) {
      alert(error.message);
    } finally {
      setReviewingVetProviderId("");
    }
  };

  const setBookingStatus = async (bookingId, status) => {
    try {
      await callAdminApi({
        action: "booking_status",
        bookingId,
        status,
      });
      await fetchAll();
    } catch (error) {
      alert(error.message);
    }
  };

  const setRefundStatus = async (bookingId, refundStatus) => {
    try {
      await callAdminApi({
        action: "refund_status",
        bookingId,
        refundStatus,
      });
      await fetchAll();
    } catch (error) {
      alert(error.message);
    }
  };

  const runPendingSweep = async () => {
    try {
      setSweeping(true);
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error("Admin session expired. Please login again.");
      const res = await fetch(SWEEP_PENDING_BOOKINGS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ pendingMinutes: 20 }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || "Sweep failed");
      }
      alert(`Sweep complete. Reassigned: ${data?.reassignedCount || 0}, waiting: ${data?.waitingCount || 0}`);
      await fetchAll();
    } catch (err) {
      alert(err.message);
    } finally {
      setSweeping(false);
    }
  };

  const toggleFeatured = async (service) => {
    const now = Date.now();
    const end = service?.featuredUntil ? new Date(service.featuredUntil).getTime() : 0;
    const active = Boolean(service.isFeatured && end > now);
    await updateDoc(doc(db, "services", service.id), {
      isFeatured: !active,
      featuredUntil: !active ? new Date(now + 30 * 24 * 60 * 60 * 1000).toISOString() : null,
    });
    await fetchAll();
  };

  const setFeaturedRequestStatus = async (requestItem, status) => {
    await updateDoc(doc(db, "featuredRequests", requestItem.id), {
      status,
      decidedAt: serverTimestamp(),
    });
    if (status === "approved" && requestItem?.serviceId) {
      await updateDoc(doc(db, "services", requestItem.serviceId), {
        isFeatured: true,
        featuredUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      });
    }
    await fetchAll();
  };
  const moderateReviewReport = async (report, moderationAction) => {
    try {
      setModeratingReportId(report.id);
      await callAdminApi({
        action: "review_moderation",
        reportId: report.id,
        reviewId: report.reviewId || "",
        moderationAction,
        note: "",
      });
      await fetchAll();
    } catch (error) {
      alert(error.message || "Unable to update review report.");
    } finally {
      setModeratingReportId("");
    }
  };

  const updatePayoutStatus = async (requestItem, status) => {
    if (!requestItem?.id) return;
    try {
      setUpdatingPayoutId(requestItem.id);
      let payoutReference = "";
      if (status === "paid") {
        payoutReference = window.prompt("Enter payout reference / transaction id (optional):") || "";
      }
      await callPayoutApi({
        action: "admin_update_status",
        requestId: requestItem.id,
        status,
        payoutReference: payoutReference.trim(),
      });
      await fetchAll();
    } catch (error) {
      alert(error.message || "Unable to update payout status.");
    } finally {
      setUpdatingPayoutId("");
    }
  };

  const summaryCards = [
    { label: "Providers", value: providers.length, meta: `${providerStats.approved} approved`, tone: "default", icon: "🧑‍⚕️" },
    { label: "Bookings", value: bookings.length, meta: `${paidBookings.length} paid`, tone: "accent", icon: "📅" },
    { label: "Delayed Assignments", value: assignmentWatchlist.length, meta: "10+ min wait", tone: "warning", icon: "⏱️" },
    { label: "Payout Requests", value: payoutStats.totalCount, meta: `Rs ${payoutStats.totalAmount.toLocaleString("en-IN")}`, tone: "success", icon: "💸" },
    { label: "Subscriptions", value: subscriptionStats.total, meta: `${subscriptionStats.active} active`, tone: "default", icon: "⭐" },
    { label: "Review Reports", value: reviewReportStats.total, meta: `${reviewReportStats.pending} pending`, tone: "danger", icon: "🛡️" },
  ];
  const sectionCardStyle = {
    padding: 24,
    display: "grid",
    gap: 14,
  };
  const rowCardStyle = {
    border: "1px solid var(--border)",
    borderRadius: 18,
    background: "rgba(255,255,255,0.9)",
    padding: 16,
    marginBottom: 10,
  };
  const filterBarStyle = {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 8,
  };

  return (
    <div className="petaar-page">
      <div style={{ maxWidth: 1260, margin: "0 auto", display: "grid", gap: 20 }}>
        <section className="petaar-card" style={sectionCardStyle}>
          <PageHeader
            eyebrow="Admin Operations"
            title="Admin Dashboard"
            subtitle="Oversee providers, bookings, payouts, subscriptions, moderation, and payment health from one control surface."
            actions={(
              <>
                <button className="petaar-button petaar-button--secondary" onClick={fetchAll} disabled={loading}>
                  {loading ? "Refreshing..." : "Refresh"}
                </button>
                <button className="petaar-button" onClick={onLogout}>Logout</button>
              </>
            )}
          />
      {loadError && (
        <div style={{ border: "1px solid #fecaca", background: "#fef2f2", color: "#991b1b", padding: 10, borderRadius: 8, marginTop: 10 }}>
          {loadError}
        </div>
      )}

          <div className="petaar-stat-grid">
            {summaryCards.map((item) => (
              <StatCard
                key={item.label}
                icon={item.icon}
                label={item.label}
                value={item.value}
                meta={item.meta}
                tone={item.tone}
              />
            ))}
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <ToneBadge tone="success">Pending partner reviews: {pendingPartnerApplications}</ToneBadge>
            <ToneBadge tone="warning">Featured requests pending: {pendingFeaturedRequests}</ToneBadge>
            <ToneBadge tone="default">Support open: {supportTicketStats.open}</ToneBadge>
            <button className="petaar-button petaar-button--accent" onClick={runPendingSweep} disabled={sweeping}>
              {sweeping ? "Running sweep..." : "Run Pending Sweep"}
            </button>
          </div>
        </section>

      <section className="petaar-card" style={sectionCardStyle}>
      <h2 style={{ margin: 0 }}>Recent Subscriptions</h2>
      {subscriptions.length === 0 ? (
        <EmptyState icon="⭐" title="No subscriptions yet" description="Featured plans and AI premium subscriptions will appear here once customers start purchasing them." />
      ) : (
        [...subscriptions]
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
          .slice(0, 40)
          .map((item) => (
            <div key={item.id} style={rowCardStyle}>
              <p style={{ margin: 0 }}>
                <strong>{item.planName || item.planCode || "Subscription"}</strong>
              </p>
              <p style={{ margin: "6px 0" }}>
                User: {item.userEmail || "N/A"} | Role: {item.userRole || "N/A"}
              </p>
              <p style={{ margin: "6px 0" }}>
                Type: {item.planType || "N/A"} | Status: {item.status || "N/A"} | Amount: Rs {Number(item.amount || 0).toLocaleString("en-IN")}
              </p>
              <p style={{ margin: "6px 0" }}>
                Start: {item.startAtIso ? new Date(item.startAtIso).toLocaleDateString("en-IN") : "N/A"} |
                End: {item.endAtIso ? new Date(item.endAtIso).toLocaleDateString("en-IN") : "N/A"}
              </p>
              {item.target?.serviceId ? (
                <p style={{ margin: "6px 0" }}>
                  Service: {item.target?.serviceTitle || "N/A"} ({item.target?.serviceId || "N/A"})
                </p>
              ) : null}
            </div>
          ))
      )}
      </section>

      <section className="petaar-card" style={sectionCardStyle}>
      <h2 style={{ margin: 0 }}>Revenue Snapshot</h2>
      <div style={filterBarStyle}>
        <select value={revenuePeriodFilter} onChange={(e) => setRevenuePeriodFilter(e.target.value)}>
          <option value="all">All Time</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
        </select>
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <div style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12, minWidth: 190 }}>
          <strong>Gross Paid</strong>
          <div>Rs {revenueStats.grossPaid.toLocaleString("en-IN")}</div>
        </div>
        <div style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12, minWidth: 190 }}>
          <strong>Refunded</strong>
          <div>Rs {revenueStats.refunded.toLocaleString("en-IN")}</div>
        </div>
        <div style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12, minWidth: 220 }}>
          <strong>Platform Commission (20%)</strong>
          <div>Rs {revenueStats.platformCommission.toLocaleString("en-IN")}</div>
        </div>
        <div style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12, minWidth: 220 }}>
          <strong>Provider Payout Estimate (80%)</strong>
          <div>Rs {revenueStats.providerPayout.toLocaleString("en-IN")}</div>
        </div>
      </div>
      <div style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12 }}>
        <strong>Last 6 Months</strong>
        {monthlyRevenue.length === 0 ? (
          <p style={{ marginBottom: 0 }}>No paid bookings yet.</p>
        ) : (
          monthlyRevenue.map((m) => (
            <div
              key={m.month}
              style={{
                marginTop: 8,
                borderTop: "1px dashed #e5e7eb",
                paddingTop: 8,
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: 8,
              }}
            >
              <div><strong>{m.month}</strong></div>
              <div>Gross: Rs {m.gross.toLocaleString("en-IN")}</div>
              <div>Refunded: Rs {m.refunded.toLocaleString("en-IN")}</div>
              <div>Commission: Rs {m.commission.toLocaleString("en-IN")}</div>
              <div>Payout: Rs {m.payout.toLocaleString("en-IN")}</div>
            </div>
          ))
        )}
      </div>
      </section>

      <section className="petaar-card" style={sectionCardStyle}>
      <h2 style={{ margin: 0 }}>Assignment Watchlist</h2>
      <p style={{ marginTop: 0, color: "#475569" }}>
        Paid bookings waiting 10+ minutes for provider/vet assignment.
      </p>
      {assignmentWatchlist.length === 0 ? (
        <EmptyState icon="⏱️" title="No delayed assignment bookings right now" description="Bookings that remain paid and unassigned for longer than 10 minutes will surface here." />
      ) : (
        assignmentWatchlist.map((item) => (
          <div key={item.id} style={rowCardStyle}>
            <p style={{ margin: 0 }}>
              <strong>{item.serviceTitle || "Service"}</strong> | Wait: <strong>{item.waitMins} min</strong>
            </p>
            <p style={{ margin: "6px 0" }}>
              Booking ID: {item.id} | Type: {item.bookingType || "service_booking"} | Status: {item.status || "unknown"}
            </p>
            <p style={{ margin: "6px 0" }}>
              Owner: {item.ownerEmail || item.userEmail || "N/A"} | Provider: {item.providerEmail || "N/A"}
            </p>
            <button onClick={runPendingSweep} disabled={sweeping}>
              {sweeping ? "Running..." : "Run Sweep Now"}
            </button>
          </div>
        ))
      )}
      </section>

      <section className="petaar-card" style={sectionCardStyle}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <h2 style={{ margin: 0 }}>Payment Health</h2>
        <button onClick={fetchPaymentHealth}>Refresh Payment Health</button>
      </div>
      {paymentHealthError && (
        <div style={{ border: "1px solid #fecaca", background: "#fef2f2", color: "#991b1b", padding: 10, borderRadius: 8, marginBottom: 10 }}>
          {paymentHealthError}
        </div>
      )}
      {!paymentHealth ? (
        <p>Payment health data unavailable.</p>
      ) : (
        <>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
            <div style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12, minWidth: 220 }}>
              <strong>Payment Config</strong>
              <div>Key ID: {paymentHealth.config?.keyIdConfigured ? "Configured" : "Missing"}</div>
              <div>Key Secret: {paymentHealth.config?.keySecretConfigured ? "Configured" : "Missing"}</div>
              <div>Webhook Secret: {paymentHealth.config?.webhookSecretConfigured ? "Configured" : "Missing"}</div>
            </div>
            <div style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12, minWidth: 220 }}>
              <strong>Payment Summary</strong>
              <div>Paid: {paymentHealth.summary?.paid || 0}</div>
              <div>Failed: {paymentHealth.summary?.failed || 0}</div>
              <div>Refund Requested: {paymentHealth.summary?.refundRequested || 0}</div>
              <div>Refunded: {paymentHealth.summary?.refunded || 0}</div>
              <div>Pending assignment &gt;30m: {paymentHealth.summary?.pendingAssignmentOver30m || 0}</div>
            </div>
            <div style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12, minWidth: 260 }}>
              <strong>Amounts</strong>
              <div>Total Paid: Rs {(paymentHealth.summary?.paidAmount || 0).toLocaleString("en-IN")}</div>
              <div>Settled Net: Rs {(paymentHealth.summary?.settledNetAmount || 0).toLocaleString("en-IN")}</div>
              <div>Last 7 Days Paid: {paymentHealth.summary?.last7DaysPaidCount || 0}</div>
              <div>Last 7 Days Amount: Rs {(paymentHealth.summary?.last7DaysPaidAmount || 0).toLocaleString("en-IN")}</div>
            </div>
          </div>
          <div style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12 }}>
            <strong>Recent Webhook Events</strong>
            {Array.isArray(paymentHealth.recentWebhookEvents) && paymentHealth.recentWebhookEvents.length > 0 ? (
              paymentHealth.recentWebhookEvents.map((item) => (
                <div key={item.id} style={{ borderTop: "1px dashed #e5e7eb", paddingTop: 8, marginTop: 8 }}>
                  <div><strong>{item.event}</strong> ({item.status})</div>
                  <div style={{ color: "#475569", fontSize: 13 }}>
                    Order: {item.orderId || "N/A"} | Payment: {item.paymentId || "N/A"} | Booking: {item.bookingId || "N/A"}
                  </div>
                  {item.message && <div style={{ color: "#475569", fontSize: 13 }}>{item.message}</div>}
                </div>
              ))
            ) : (
              <p style={{ marginBottom: 0 }}>No webhook events logged yet.</p>
            )}
          </div>
        </>
      )}
      </section>

      <section className="petaar-card" style={sectionCardStyle}>
      <h2 style={{ margin: 0 }}>Provider Payout Requests</h2>
      {payoutLoadError && (
        <div style={{ border: "1px solid #fecaca", background: "#fef2f2", color: "#991b1b", padding: 10, borderRadius: 8, marginBottom: 10 }}>
          {payoutLoadError}
        </div>
      )}
      <div style={filterBarStyle}>
        <select value={payoutStatusFilter} onChange={(e) => setPayoutStatusFilter(e.target.value)}>
          <option value="">All Payout Status</option>
          <option value="requested">requested</option>
          <option value="approved">approved</option>
          <option value="paid">paid</option>
          <option value="rejected">rejected</option>
          <option value="cancelled">cancelled</option>
        </select>
        <button onClick={fetchPayoutRequests}>Refresh Payout Requests</button>
      </div>
      {sortedFilteredPayoutRequests.length === 0 ? (
        <EmptyState icon="💸" title="No payout requests yet" description="Provider withdrawal requests will appear here for approval and settlement." />
      ) : (
        pagedPayoutRequests
          .map((item) => (
            <div key={item.id} style={rowCardStyle}>
              <p style={{ margin: 0 }}>
                <strong>{item.providerEmail || "Provider"}</strong> | Amount: Rs {(Number(item.amount) || 0).toLocaleString("en-IN")}
              </p>
              <p style={{ margin: "6px 0" }}>
                Status: <strong style={{ textTransform: "capitalize" }}>{item.status || "requested"}</strong> | Mode: {item.payoutMode || "N/A"}
              </p>
              <p style={{ margin: "6px 0" }}>
                Account: {item.payoutAccount || "N/A"} | Reference: {item.payoutReference || "N/A"}
              </p>
              {item.note && <p style={{ margin: "6px 0" }}>Provider Note: {item.note}</p>}
              {item.adminNote && <p style={{ margin: "6px 0" }}>Admin Note: {item.adminNote}</p>}
              {item.status === "requested" && (
                <>
                  <button
                    onClick={() => updatePayoutStatus(item, "approved")}
                    disabled={updatingPayoutId === item.id}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => updatePayoutStatus(item, "rejected")}
                    style={{ marginLeft: 8 }}
                    disabled={updatingPayoutId === item.id}
                  >
                    Reject
                  </button>
                </>
              )}
              {item.status === "approved" && (
                <>
                  <button
                    onClick={() => updatePayoutStatus(item, "paid")}
                    disabled={updatingPayoutId === item.id}
                  >
                    Mark Paid
                  </button>
                  <button
                    onClick={() => updatePayoutStatus(item, "rejected")}
                    style={{ marginLeft: 8 }}
                    disabled={updatingPayoutId === item.id}
                  >
                    Reject
                  </button>
                </>
              )}
            </div>
          ))
      )}
      {sortedFilteredPayoutRequests.length > 0 && totalPayoutPages > 1 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
          <button onClick={() => setPayoutsPage(1)} disabled={payoutsPage <= 1}>First</button>
          <button onClick={() => setPayoutsPage((prev) => Math.max(1, prev - 1))} disabled={payoutsPage <= 1}>
            Previous
          </button>
          <span>Page {payoutsPage} of {totalPayoutPages}</span>
          <button onClick={() => setPayoutsPage((prev) => Math.min(totalPayoutPages, prev + 1))} disabled={payoutsPage >= totalPayoutPages}>
            Next
          </button>
          <button onClick={() => setPayoutsPage(totalPayoutPages)} disabled={payoutsPage >= totalPayoutPages}>Last</button>
        </div>
      )}
      </section>

      <section className="petaar-card" style={sectionCardStyle}>
      <h2 style={{ margin: 0 }}>Vet Verification Queue</h2>
      {vetApplications.length === 0 ? (
        <EmptyState icon="🩺" title="No vet applications found" description="Approved and pending vet verification submissions will appear here for review." />
      ) : (
        [...vetApplications]
          .sort((a, b) => {
            const aStatus = String(a.status || "").toLowerCase();
            const bStatus = String(b.status || "").toLowerCase();
            if (aStatus === "pending" && bStatus !== "pending") return -1;
            if (bStatus === "pending" && aStatus !== "pending") return 1;
            return (b.submittedAt?.seconds || 0) - (a.submittedAt?.seconds || 0);
          })
          .map((item) => (
            <div key={item.id} style={rowCardStyle}>
              <p style={{ margin: 0 }}>
                <strong>{item.providerEmail || item.vetProfile?.displayName || "Provider"}</strong>
              </p>
              <p style={{ margin: "6px 0" }}>
                Status: <strong style={{
                  textTransform: "capitalize",
                  color:
                    String(item.status || "").toLowerCase() === "approved" ? "#166534" :
                    String(item.status || "").toLowerCase() === "rejected" ? "#b91c1c" :
                    "#92400e",
                }}>{item.status || "pending"}</strong> |
                Vet Score: <strong>{Number(item.vetScore || 0).toFixed(1)}/10</strong>
              </p>
              <p style={{ margin: "6px 0" }}>
                Registration: {item.vetProfile?.registrationNumber || "N/A"} | Workplace: {item.vetProfile?.currentWorkplace || "N/A"}
              </p>
              <p style={{ margin: "6px 0" }}>
                Location: {item.vetProfile?.location || "N/A"} | Experience: {item.vetProfile?.yearsOfService || 0}+ years
              </p>
              <p style={{ margin: "6px 0", color: item.aiDocDetection?.suspicious ? "#b91c1c" : "#166534" }}>
                AI Doc Detection: {item.aiDocDetection?.suspicious ? "Suspicious files detected" : "No suspicious patterns"}
              </p>
              <p style={{ margin: "6px 0" }}>
                Documents: {Array.isArray(item.documents) ? item.documents.length : 0}
              </p>
              {Array.isArray(item.scoringBreakdown) && item.scoringBreakdown.length > 0 && (
                <details style={{ marginBottom: 8 }}>
                  <summary style={{ cursor: "pointer", fontWeight: 700 }}>Score Breakdown</summary>
                  <ul style={{ margin: "8px 0 0 18px", padding: 0 }}>
                    {item.scoringBreakdown.map((row, idx) => (
                      <li key={`${item.id}-score-${idx}`} style={{ marginBottom: 2 }}>
                        {row?.label || "Criteria"}: {Number(row?.points || 0).toFixed(1)}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {Array.isArray(item.documents) && item.documents.length > 0 && (
                <details style={{ marginBottom: 8 }}>
                  <summary style={{ cursor: "pointer", fontWeight: 700 }}>View Uploaded Documents</summary>
                  <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
                    {item.documents.map((docItem, idx) => {
                      const docUrl = String(docItem?.url || "").trim();
                      const docName = String(docItem?.name || `Document ${idx + 1}`).trim();
                      const docSize = Number(docItem?.size || 0);
                      const sizeLabel = docSize > 0 ? `${(docSize / (1024 * 1024)).toFixed(2)} MB` : "Unknown size";
                      return (
                        <a
                          key={`${item.id}-doc-${idx}`}
                          href={docUrl || "#"}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            color: docUrl ? "#1d4ed8" : "#64748b",
                            textDecoration: "none",
                            pointerEvents: docUrl ? "auto" : "none",
                          }}
                        >
                          {docName} ({sizeLabel})
                        </a>
                      );
                    })}
                  </div>
                </details>
              )}
              {Array.isArray(item.aiDocDetection?.findings) && item.aiDocDetection.findings.length > 0 && (
                <details style={{ marginBottom: 8 }}>
                  <summary style={{ cursor: "pointer", fontWeight: 700, color: "#b91c1c" }}>
                    AI Detection Findings
                  </summary>
                  <ul style={{ margin: "8px 0 0 18px", padding: 0 }}>
                    {item.aiDocDetection.findings.map((finding, idx) => (
                      <li key={`${item.id}-finding-${idx}`} style={{ marginBottom: 2 }}>
                        {String(finding?.name || "document")} -> {(finding?.tokens || []).join(", ")}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              <button
                onClick={() => reviewVetApplication(item.providerId || item.id, "approved")}
                disabled={reviewingVetProviderId === (item.providerId || item.id)}
              >
                Approve Vet
              </button>
              <button
                onClick={() => reviewVetApplication(item.providerId || item.id, "rejected")}
                style={{ marginLeft: 8 }}
                disabled={reviewingVetProviderId === (item.providerId || item.id)}
              >
                Reject Vet
              </button>
            </div>
          ))
      )}
      </section>

      <section className="petaar-card" style={sectionCardStyle}>
      <h2 style={{ margin: 0 }}>Provider Approvals</h2>
      {providers.length === 0 ? (
        <EmptyState icon="🧾" title="No provider user docs found" description="Provider accounts appear here when they start onboarding or submit applications." />
      ) : (
        providers.map((p) => (
          <div key={p.id} style={rowCardStyle}>
            <p style={{ margin: 0 }}><strong>{p.email || p.phone || p.id}</strong></p>
            <p style={{ margin: "6px 0" }}>Status: {p.providerStatus || "pending"}</p>
            <button onClick={() => setProviderStatus(p.id, "approved")}>Approve</button>
            <button onClick={() => setProviderStatus(p.id, "rejected")} style={{ marginLeft: 8 }}>Reject</button>
            <button onClick={() => setProviderStatus(p.id, "pending")} style={{ marginLeft: 8 }}>Set Pending</button>
          </div>
        ))
      )}
      </section>

      <section className="petaar-card" style={sectionCardStyle}>
      <h2 style={{ margin: 0 }}>Partner Applications</h2>
      {partnerApplications.length === 0 ? (
        <EmptyState icon="📥" title="No partner applications found" description="Submitted partner onboarding packets will appear here for admin review." />
      ) : (
        [...partnerApplications]
          .sort((a, b) => (b.submittedAt?.seconds || 0) - (a.submittedAt?.seconds || 0))
          .map((item) => (
            <div key={item.id} style={rowCardStyle}>
              <p style={{ margin: 0 }}><strong>{item.providerEmail || item.id}</strong></p>
              <p style={{ margin: "6px 0" }}>
                Application Status: {String(item.status || "pending_review")}
              </p>
              <button onClick={() => setProviderStatus(item.providerId || item.id, "approved")}>Approve Provider</button>
              <button onClick={() => setProviderStatus(item.providerId || item.id, "rejected")} style={{ marginLeft: 8 }}>
                Reject Provider
              </button>
              <button onClick={() => setProviderStatus(item.providerId || item.id, "pending")} style={{ marginLeft: 8 }}>
                Keep Pending
              </button>
            </div>
          ))
      )}
      </section>

      <section className="petaar-card" style={sectionCardStyle}>
      <h2 style={{ margin: 0 }}>Booking Oversight</h2>
      <div style={filterBarStyle}>
        <select value={bookingStatusFilter} onChange={(e) => setBookingStatusFilter(e.target.value)}>
          <option value="">All Booking Status</option>
          <option value="pending_assignment">pending_assignment</option>
          <option value="pending">pending</option>
          <option value="accepted">accepted</option>
          <option value="in_progress">in_progress</option>
          <option value="completed">completed</option>
          <option value="cancel_requested">cancel_requested</option>
          <option value="cancelled">cancelled</option>
        </select>
        <select value={paymentStatusFilter} onChange={(e) => setPaymentStatusFilter(e.target.value)}>
          <option value="">All Payment Status</option>
          <option value="paid">paid</option>
          <option value="pending">pending</option>
          <option value="failed">failed</option>
        </select>
        <select value={refundStatusFilter} onChange={(e) => setRefundStatusFilter(e.target.value)}>
          <option value="">All Refund Status</option>
          <option value="refund_requested">refund_requested</option>
          <option value="refund_processed">refund_processed</option>
          <option value="not_applicable">not_applicable</option>
        </select>
        <button onClick={() => {
          setBookingStatusFilter("");
          setPaymentStatusFilter("");
          setRefundStatusFilter("");
        }}>Clear</button>
        <button onClick={exportBookingsCsv}>Export CSV</button>
      </div>
      <p style={{ marginTop: 0, color: "#475569" }}>
        Showing {((bookingsPage - 1) * adminPageSize) + (filteredAdminBookings.length ? 1 : 0)}-
        {Math.min(bookingsPage * adminPageSize, filteredAdminBookings.length)} of {filteredAdminBookings.length}
      </p>
      {filteredAdminBookings.length === 0 ? (
        <EmptyState icon="📅" title="No bookings found" description="Use the filters above to narrow results, or wait for booking traffic to populate this section." />
      ) : (
        pagedAdminBookings
          .map((b) => (
          <div key={b.id} style={rowCardStyle}>
            <p style={{ margin: 0 }}><strong>{b.serviceTitle || "Service"}</strong></p>
            <p style={{ margin: "6px 0" }}>Owner: {b.ownerEmail || b.userEmail || "N/A"}</p>
            <p style={{ margin: "6px 0" }}>Provider: {b.providerEmail || "N/A"}</p>
            <p style={{ margin: "6px 0" }}>
              Date/Slot: {b.bookingDate || "N/A"}{b.bookingSlot ? ` | ${b.bookingSlot}` : ""}
            </p>
            <p style={{ margin: "6px 0" }}>Payment: {b.paymentStatus || "unknown"} | Status: {b.status || "unknown"}</p>
            <p style={{ margin: "6px 0" }}>Refund: {b.refundStatus || "not_requested"}</p>
            {b.cancellationReason && (
              <p style={{ margin: "6px 0" }}>Cancel Reason: {b.cancellationReason}</p>
            )}
            <button onClick={() => setBookingStatus(b.id, "completed")}>Mark Completed</button>
            <button onClick={() => setBookingStatus(b.id, "cancelled")} style={{ marginLeft: 8 }}>Cancel</button>
            {b.refundStatus === "refund_requested" && (
              <button onClick={() => setRefundStatus(b.id, "refund_processed")} style={{ marginLeft: 8 }}>
                Mark Refund Processed
              </button>
            )}
          </div>
        ))
      )}
      {filteredAdminBookings.length > 0 && totalBookingPages > 1 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
          <button onClick={() => setBookingsPage(1)} disabled={bookingsPage <= 1}>First</button>
          <button onClick={() => setBookingsPage((prev) => Math.max(1, prev - 1))} disabled={bookingsPage <= 1}>
            Previous
          </button>
          <span>Page {bookingsPage} of {totalBookingPages}</span>
          <button onClick={() => setBookingsPage((prev) => Math.min(totalBookingPages, prev + 1))} disabled={bookingsPage >= totalBookingPages}>
            Next
          </button>
          <button onClick={() => setBookingsPage(totalBookingPages)} disabled={bookingsPage >= totalBookingPages}>Last</button>
        </div>
      )}
      </section>

      <section className="petaar-card" style={sectionCardStyle}>
      <h2 style={{ margin: 0 }}>Featured Listing Controls</h2>
      {services.length === 0 ? (
        <EmptyState icon="✨" title="No services found" description="Published service listings will appear here for featured placement control." />
      ) : (
        pagedServices.map((s) => (
          <div key={s.id} style={rowCardStyle}>
            <p style={{ margin: 0 }}><strong>{s.title}</strong> ({s.city || "N/A"})</p>
            <p style={{ margin: "6px 0" }}>Provider: {s.providerEmail || "N/A"}</p>
            <p style={{ margin: "6px 0" }}>
              Featured: {s.isFeatured && s.featuredUntil &&
              new Date(s.featuredUntil).getTime() > Date.now()
                ? `Yes (till ${new Date(s.featuredUntil).toLocaleDateString()})`
                : "No"}
            </p>
            <button onClick={() => toggleFeatured(s)}>
              {s.isFeatured && s.featuredUntil &&
              new Date(s.featuredUntil).getTime() > Date.now()
                ? "Disable Featured"
                : "Enable Featured (30 days)"}
            </button>
          </div>
        ))
      )}
      {services.length > 0 && totalServicePages > 1 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
          <button onClick={() => setServicesPage(1)} disabled={servicesPage <= 1}>First</button>
          <button onClick={() => setServicesPage((prev) => Math.max(1, prev - 1))} disabled={servicesPage <= 1}>
            Previous
          </button>
          <span>Page {servicesPage} of {totalServicePages}</span>
          <button onClick={() => setServicesPage((prev) => Math.min(totalServicePages, prev + 1))} disabled={servicesPage >= totalServicePages}>
            Next
          </button>
          <button onClick={() => setServicesPage(totalServicePages)} disabled={servicesPage >= totalServicePages}>Last</button>
        </div>
      )}
      </section>

      <section className="petaar-card" style={sectionCardStyle}>
      <h2 style={{ margin: 0 }}>Featured Listing Requests</h2>
      {featuredRequests.length === 0 ? (
        <EmptyState icon="📌" title="No featured requests yet" description="Manual featured placement requests from providers will surface here." />
      ) : (
        featuredRequests
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
          .map((r) => (
            <div key={r.id} style={rowCardStyle}>
              <p style={{ margin: 0 }}><strong>{r.serviceTitle || "Service"}</strong></p>
              <p style={{ margin: "6px 0" }}>Provider: {r.providerEmail || "N/A"}</p>
              <p style={{ margin: "6px 0" }}>City: {r.city || "N/A"} | Category: {r.category || "N/A"}</p>
              <p style={{ margin: "6px 0" }}>Status: {r.status || "pending"}</p>
              <button onClick={() => setFeaturedRequestStatus(r, "approved")}>Approve</button>
              <button onClick={() => setFeaturedRequestStatus(r, "rejected")} style={{ marginLeft: 8 }}>Reject</button>
            </div>
          ))
      )}
      </section>

      <section className="petaar-card" style={sectionCardStyle}>
      <h2 style={{ margin: 0 }}>Review Reports</h2>
      {reviewReports.length === 0 ? (
        <EmptyState icon="🚨" title="No review reports yet" description="Reported reviews needing moderation will show here." />
      ) : (
        reviewReports
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
          .slice(0, 60)
          .map((report) => (
            <div key={report.id} style={rowCardStyle}>
              <p style={{ margin: 0 }}><strong>{report.serviceTitle || "Service review report"}</strong></p>
              <p style={{ margin: "6px 0" }}>
                Review ID: {report.reviewId || "N/A"} | Status: {report.status || "pending"}
              </p>
              <p style={{ margin: "6px 0" }}>
                Reporter: {report.reporterEmail || "N/A"} | Provider: {report.providerEmail || "N/A"}
              </p>
              <p style={{ margin: "6px 0" }}>
                Reason: {report.reason || "N/A"}
              </p>
              <button
                onClick={() => moderateReviewReport(report, "dismiss")}
                disabled={moderatingReportId === report.id}
              >
                Dismiss
              </button>
              <button
                onClick={() => moderateReviewReport(report, "hide")}
                style={{ marginLeft: 8 }}
                disabled={moderatingReportId === report.id}
              >
                Hide Review
              </button>
              <button
                onClick={() => moderateReviewReport(report, "unhide")}
                style={{ marginLeft: 8 }}
                disabled={moderatingReportId === report.id}
              >
                Unhide Review
              </button>
            </div>
          ))
      )}
      </section>

      <section className="petaar-card" style={sectionCardStyle}>
      <h2 style={{ margin: 0 }}>Moderation Logs</h2>
      {moderationLogs.length === 0 ? (
        <EmptyState icon="🗂️" title="No moderation logs yet" description="Completed moderation actions will be listed here for audit visibility." />
      ) : (
        moderationLogs
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
          .slice(0, 80)
          .map((log) => (
            <div key={log.id} style={rowCardStyle}>
              <p style={{ margin: 0 }}>
                <strong>{log.entityType || "review"} moderation</strong> | Action: {log.action || "N/A"}
              </p>
              <p style={{ margin: "6px 0" }}>
                Review: {log.entityId || "N/A"} | Report: {log.reportId || "N/A"}
              </p>
              <p style={{ margin: "6px 0" }}>
                Admin: {log.adminEmail || "N/A"} | Reporter: {log.reporterEmail || "N/A"}
              </p>
              <p style={{ margin: "6px 0" }}>
                Reason: {log.reason || "N/A"}
              </p>
              {log.note && (
                <p style={{ margin: "6px 0" }}>
                  Note: {log.note}
                </p>
              )}
            </div>
          ))
      )}
      </section>
    </div>
    </div>
  );
}
